import React, { HTMLAttributes, useEffect, useState } from 'react';
import { useCustomNavigate } from '@/website/hooks/useCustomNavigate';
import { Routes } from '@/website/util/routes';
import { JoveButton } from '@jove-frontend/mui';
import { openUrlWithNewWindow, useJoveBreakpoint } from '@jove-frontend/uikit';
import { ApartmentOutlined, BusinessCenter, CallMadeOutlined, FileDownload, Layers, NorthWestOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';

interface OurSchemeListInfoItemInterface {
  value: string;
  title: string;
  content: string;
  descList: Array<{ label: string }>;
  icon?: 'Layers' | 'BusinessCenter' | 'ApartmentOutlined';
  iconImage?: React.ReactNode;
  index: number;
  brochureUrl: string;
}

type BrokerOurSchemesViewProps = HTMLAttributes<HTMLDivElement> & {
  info: {
    ourSchemesSectionTitle: string;
    ourSchemesTitle: string;
    ourSchemesDesc: string;
    ourSchemesInfoList: OurSchemeListInfoItemInterface[];
    ourSchemesBookDemoUrl: string;
    bookMeetingButtonText: string;
    activateBrokerAccessButtonText: string;
    downloadBrochureButtonText: string;
  };
};

const BrokerOurSchemesView = (props: BrokerOurSchemesViewProps) => {
  const { className, info } = props;
  const { isMobileLayout } = useJoveBreakpoint();
  const [schemeList, setSchemeList] = useState<OurSchemeListInfoItemInterface[]>([]);
  const [currentScheme, setCurrentScheme] = useState('');
  const { push } = useCustomNavigate();

  /**
   * effects
   */
  useEffect(() => {
    if (info?.ourSchemesInfoList) {
      const newList = info.ourSchemesInfoList.map((item, index) => {
        if (item.icon === 'ApartmentOutlined') {
          item.iconImage = <ApartmentOutlined className="text-brand-secondary h-8 w-8" />;
        } else if (item.icon === 'BusinessCenter') {
          item.iconImage = <BusinessCenter className="text-brand-secondary h-8 w-8" />;
        } else {
          item.iconImage = <Layers className="text-brand-secondary h-8 w-8" />;
        }
        item.index = index;
        return item;
      });
      setSchemeList(newList);
      setCurrentScheme(newList?.[0]?.value || '');
    }
  }, [info]);

  /**
   * methods
   */
  const updateSchemeAction = (item) => {
    setCurrentScheme(item.value);
  };

  const hideSchemeAction = (item) => {
    if (item.index === 0) {
      setCurrentScheme(schemeList[schemeList.length - 1].value);
    } else {
      setCurrentScheme(schemeList[item.index - 1].value);
    }
  };

  const downloadBrochureAction = (item) => {
    openUrlWithNewWindow(item.brochureUrl);
  };
  const bookMeetingAction = () => {
    openUrlWithNewWindow(info.ourSchemesBookDemoUrl);
  };

  const goToBrokerOnboardingAction = () => {
    push({ path: Routes.brokerOnboardingSubmit() });
  };

  return (
    <Box className={clsx('flex flex-col items-center', className)}>
      <Box className={clsx('flex flex-col items-center', isMobileLayout ? 'w-full space-y-6' : 'w-[960px] space-y-12')}>
        <Box className="flex flex-col items-center space-y-4">
          <Box className="flex flex-row rounded-full bg-[#EBEFFE] px-3 py-2">
            <Typography variant="subtitle1" className="text-brand-secondary font-extrabold">
              {info?.ourSchemesSectionTitle}
            </Typography>
          </Box>
          <Typography variant="h4" className="text-primary-black text-center font-black">
            {info?.ourSchemesTitle}
          </Typography>
          <Typography variant="h6">{info?.ourSchemesDesc}</Typography>
        </Box>
        <Box className={clsx('flex gap-4', isMobileLayout ? 'flex-col' : 'flex-row')}>
          {schemeList.map((item, index) => {
            return (
              <Box
                key={index}
                className={clsx(
                  'flex flex-col justify-between rounded-2xl bg-white',
                  currentScheme === item.value ? 'px-6 py-4' : 'cursor-pointer p-4'
                )}
                style={{ boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.08)' }}
                onClick={currentScheme === item.value ? null : () => updateSchemeAction(item)}
              >
                <Box className={clsx('flex flex-col space-y-2')}>
                  <Box className="flex flex-row items-center space-x-4">
                    <Box className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EBEFFE]">{item.iconImage}</Box>
                    {(currentScheme === item.value || isMobileLayout) && (
                      <Typography variant="h5" className="text-primary-black flex-1 font-black">
                        {item.title}
                      </Typography>
                    )}
                  </Box>

                  {currentScheme === item.value && (
                    <Box className="flex flex-col space-y-3">
                      <JoveButton
                        startIcon={<FileDownload />}
                        variant="contained"
                        size="small"
                        className="self-end"
                        onClick={() => downloadBrochureAction(item)}
                      >
                        {info?.downloadBrochureButtonText}
                      </JoveButton>
                      <Typography variant="h6" className="text-primary-black font-bold">
                        {item.content}
                      </Typography>
                      <Box className="flex flex-col">
                        {item.descList.map((aItem, aIndex) => {
                          return (
                            <Box key={aIndex} className="flex flex-row space-x-2">
                              <Box className="bg-brand-secondary mt-3 h-2 w-2 rounded-full"></Box>
                              <Typography variant="h6" className="text-primary-black flex-1 font-medium">
                                {aItem.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      <Box className="flex flex-row justify-end">
                        <Box
                          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#f5f5f5]"
                          onClick={() => hideSchemeAction(item)}
                        >
                          <NorthWestOutlined className="text-primary-black h-6 w-6" />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
                {currentScheme !== item.value && (
                  <Box className={clsx('flex flex-row', currentScheme === item.value ? 'justify-end' : 'justify-center')}>
                    <Box className="bg-brand-secondary flex h-10 w-10 items-center justify-center rounded-full">
                      <CallMadeOutlined className="h-6 w-6 text-white" />
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Box className={clsx('flex', isMobileLayout ? 'flex-col space-y-2' : 'flex-row space-x-5')}>
          <JoveButton size="large" onClick={bookMeetingAction}>
            {info?.bookMeetingButtonText}
          </JoveButton>
          <JoveButton size="large" variant="contained" onClick={goToBrokerOnboardingAction}>
            {info?.activateBrokerAccessButtonText}
          </JoveButton>
        </Box>
      </Box>
    </Box>
  );
};

export default BrokerOurSchemesView;
